<template>
  <div class="container">
    <h1 class="subs-title">{{ adminText('organizationAll.list_of_subscription_plans_e0640013', 'List of Subscription Plans') }}</h1>
    <p class="page-description">{{ adminText('organizationAll.add_and_manage_your_subscriptions_16ba5ef5', 'Add and manage your Subscriptions.') }}</p>
    <main-button style="max-width: 244px; margin-bottom: 1rem" @click="$emit('createNewPlan')">
      <span class="add-symbol">+</span>{{ adminText('organizationAll.create_new_plan_5a737097', 'Create new plan') }}
    </main-button>
    <div v-if="!$apollo.loading" class="attendee_table">
      <div
        v-if="subscriptionPlans.length === 0"
        class="no-attendees-message-container border__left border__right"
      >
        <span class="no-attendees-message">{{ adminText('organizationAll.no_matching_records_found_0c7f0a64', 'No matching records found.') }}</span>
      </div>
      <selected-items-header
        v-if="selected.length"
        :selected="selected"
        name="subscriber"
        @clear="selected = []"
        @delete="showDeleteDialog = true"
      />
      <table-list
        :selected.sync="selected"
        :organization-slug="organizationSlug"
        :attendees="subscriptionPlans"
        body-style="grid-auto-columns: 50px minmax(150px, 250px) !important;"
        :visible-fields="visibleFields"
        :attendee-fields="attendeeFields"
        :pagination="pagination"
        @click="$emit('editPlan', $event)"
        @sort="handleSort($event)"
      />
      <table-pagination
        :pagination="pagination"
        :total-attendees="totalSubscribersCount"
        @change="handlePagination($event)"
        @changePerPage="handlePagination($event)"
      />
      <h1 class="subs-title" style="margin-top: 2.5rem">{{ adminText('organizationAll.manage_cover_for_all_subscription_plans_df33e10f', 'Manage cover for all Subscription Plans') }}</h1>
      <p class="page-description">
        {{ adminText('organizationAll.here_you_can_add_or_edit_a_universal_cover_th_8f14588a', 'Here you can add or edit a universal cover that will apply to all subscription plans.') }}
      </p>
      <cover-field v-model="coverUrl" :disable-delete="true" />
    </div>
    <delete-dialog
      :content="`${adminText('organizationAll.are_you_sure_you_want_to_delete_selected_subs_8e2fe92f', 'Are you sure you want to delete selected subscribers? This operation cannot be undone.')}`"
      :on-delete="handleDelete"
      :show.sync="showDeleteDialog"
      :title="adminText('organizationAll.delete_subscribers_6b70678a', 'Delete subscribers')"
    />
    <simple-snackbar :open.sync="snackbarOpen" :message-prop="organizationText(snackbarMessage)" color="primary" />
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import selectedItemsHeader from '@/components/tickets/selected-items-header.vue'
import deleteDialog from '../../shared/dialogs/delete-dialog.vue'
import tableList from '../../accreditations/attendee-table/list.vue'
import tablePagination from '../../accreditations/attendee-table/pagination.vue'
import { UPDATE_IMAGE_URLS } from '@/graphql/organization.graphql'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'

export default {
  mixins: [OrganizationLocalization],
  name: 'sport-subscription-plan-list',
  components: {
    SimpleSnackbar,
    deleteDialog,
    selectedItemsHeader,
    tableList,
    tablePagination,
  },
  props: {
    organizationSlug: {
      type: String,
    },
    initialValues: {
      type: Array,
      default: () => [],
    },
    organization: Object,
  },
  data() {
    const { pagination } = this.getInitialFromQueryParams()

    return {
      subscriptionPlans: this.initialValues,
      pagination,
      showDeleteDialog: false,
      openCreateModal: false,
      openUpdateModal: false,
      selected: [],
      attendeeFields: {},
      visibleFields: ['Name', 'Season', 'Duration'],
      totalSubscribersCount: 0,
      coverUrl: this.organization.imageUrls.subscriptionCoverUrl || null,
      snackbarOpen: false,
      snackbarMessage: '',
    }
  },
  watch: {
    async coverUrl() {
      const { errors } = await this.$apollo.mutate({
        mutation: UPDATE_IMAGE_URLS,
        variables: {
          organizationSlug: this.organizationSlug,
          imageUrls: { ...this.organization.imageUrls, subscriptionCoverUrl: this.coverUrl },
        },
      })
      if (errors) {
        this.snackbarMessage = 'Something went wrong. Try again.'
        this.snackbarOpen = true
      }

      this.$emit('refetch')
    },
  },
  methods: {
    handleDelete() {
      this.$emit('delete', this.selected)
      this.selected = []
    },
    async handlePagination({ page, per_page }) {
      const { page: currentPage, per_page: currentPerPage } = this.pagination
      if (currentPage > page && currentPage === 1) return
      if (page > currentPage && !(this.totalSubscribersCount - currentPerPage * currentPage > 0))
        return

      this.pagination = {
        ...this.pagination,
        per_page: per_page === 'All' ? -1 : per_page,
        page: page,
      }

      await this.addPaginationToRoute(this.pagination)
    },
    async handleSort(sortBy) {
      const { per_page, page, sortBy: thisSortBy, descending } = this.pagination
      const pagination = {
        page,
        per_page,
        sortBy,
        descending: sortBy === thisSortBy ? !descending : false,
      }
      await this.addPaginationToRoute(pagination)
      this.pagination = pagination
    },
    getInitialFromQueryParams() {
      const { per_page, page, sortBy, descending } = this.$route.query

      return {
        pagination: {
          page: parseInt(page, 10) || 1,
          per_page: parseInt(per_page, 10) || 10,
          sortBy: sortBy,
          descending: descending ? !!descending : false,
        },
      }
    },
    async addPaginationToRoute({ page, per_page, sortBy, descending }) {
      await this.$router.replace({
        query: {
          ...this.$route.query,
          per_page,
          page,
          sortBy,
          descending: descending ? 1 : 0,
        },
      })
    },
  },
}
</script>

<style scoped>
.page-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #979797;
}
.container {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  max-width: 43.75rem;
}
.subs-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
</style>
